import React, { useEffect, useMemo, useState } from "react";

export default function StateCheckIn({ token, user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ summary: null, districts: [] });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 400);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch("https://smartcoreapi.ssfkerala.org/state/checkin.php", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          throw new Error("Failed to load state check-in data");
        }
        const json = await res.json();
        if (!active) return;
        setData({ summary: json.summary ?? null, districts: json.districts ?? [] });
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load state check-in data");
        setData({ summary: null, districts: [] });
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [token]);

  const filteredDistricts = useMemo(() => {
    if (!debouncedSearch) return data.districts;
    const term = debouncedSearch;
    return data.districts.reduce((acc, district) => {
      const districtName = (district?.district?.orgname ?? "").toLowerCase();
      const divisionMatches = (district.divisions ?? []).filter((division) =>
        (division?.orgname ?? "").toLowerCase().includes(term)
      );
      if (districtName.includes(term)) {
        acc.push({ ...district, divisions: district.divisions ?? [] });
      } else if (divisionMatches.length > 0) {
        acc.push({ ...district, divisions: divisionMatches });
      }
      return acc;
    }, []);
  }, [data.districts, debouncedSearch]);

  const formatStatus = (status) => {
    if (!status) return "Not scheduled";
    return status.replace(/_/g, " ");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-gray-900">State Check-In Overview</h2>
          <p className="text-sm text-gray-500">{user.orgname}</p>
          {data.summary && (
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 sm:grid-cols-3 lg:grid-cols-6">
              <div>
                <div className="text-xs uppercase text-gray-400">Districts</div>
                <div className="text-base font-semibold">{data.summary.total_districts}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Divisions</div>
                <div className="text-base font-semibold text-blue-600">{data.summary.total_divisions}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Scheduled divisions</div>
                <div className="text-base font-semibold text-indigo-600">{data.summary.scheduled_divisions}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Open today</div>
                <div className="text-base font-semibold text-green-600">{data.summary.open_today_divisions}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Past due</div>
                <div className="text-base font-semibold text-red-600">{data.summary.past_due_divisions}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Checked-in members</div>
                <div className="text-base font-semibold text-purple-600">{data.summary.checked_in_members}</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 md:w-80">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search district or division"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="bg-white py-12 text-center shadow-sm">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : filteredDistricts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center text-gray-500">
          No matching districts.
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDistricts.map((item) => {
            const district = item.district ?? {};
            const summary = item.summary ?? {};
            return (
              <div key={district.id || district.orgname} className="space-y-3 rounded-2xl border bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{district.orgname}</div>
                    <div className="text-xs uppercase tracking-wide text-gray-400">District</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-500 md:grid-cols-4">
                    <div>
                      <div className="uppercase text-gray-400">Divisions</div>
                      <div className="text-sm font-semibold">{summary.total_divisions ?? 0}</div>
                    </div>
                    <div>
                      <div className="uppercase text-gray-400">Scheduled</div>
                      <div className="text-sm font-semibold text-indigo-600">{summary.scheduled ?? 0}</div>
                    </div>
                    <div>
                      <div className="uppercase text-gray-400">Open today</div>
                      <div className="text-sm font-semibold text-green-600">{summary.open_today ?? 0}</div>
                    </div>
                    <div>
                      <div className="uppercase text-gray-400">Past due</div>
                      <div className="text-sm font-semibold text-red-600">{summary.past_due ?? 0}</div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {(item.divisions ?? []).map((division) => {
                    const totalMembers = division.total_members ?? 0;
                    const checkedIn = division.checked_in_count ?? 0;
                    const percentage = totalMembers > 0 ? Math.round((checkedIn / totalMembers) * 100) : 0;
                    const checkinOpen = !!division.checkin_open;
                    return (
                      <div key={division.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{division.orgname}</div>
                            <div className="text-[11px] uppercase tracking-wide text-gray-400">Division</div>
                          </div>
                          <span
                            className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                              checkinOpen
                                ? "bg-green-100 text-green-700"
                                : division.status === "past_due"
                                  ? "bg-red-100 text-red-700"
                                  : division.status === "scheduled_upcoming"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {checkinOpen ? "Open today" : formatStatus(division.status)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <div>{`Program date: ${division.program_date || "Not set"}`}</div>
                          <div>{`Members checked in: ${checkedIn} / ${totalMembers}`}</div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-white">
                            <div
                              className={`h-full rounded-full ${percentage >= 75 ? "bg-green-500" : "bg-blue-500"}`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px] text-gray-500">
                          <div className="rounded-full bg-white px-3 py-1">Checked today: {division.checked_in_today ?? 0}</div>
                          <div className="rounded-full bg-white px-3 py-1">Override: {division.override_enabled ? "Yes" : "No"}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
